import React, { useState } from 'react';

import { useAppDispatch } from '../hooks';

import { EmployeeActions } from '../actions/employee/employee.actions';

import type { Employee } from '../interfaces/employee/employee.interface';

import Modal from './components/Modal';

// =============================================================================
//  Employee edit modal (v3)
//
//  Edits the lightweight HR fields the v3 redesign added or surfaces:
//
//    erole          — job title (free text, e.g. "Associate Director")
//    team           — Advisory / Sponsors / Compliance / Operations
//    office         — Cape Town / Johannesburg / Sandton (free-text)
//    qualifications — comma-separated quals string
//    mobile_number  — contact number
// =============================================================================

const TEAM_OPTIONS = [
  { value: '', label: '— Unassigned —' },
  { value: 'advisory', label: 'Advisory' },
  { value: 'sponsors', label: 'Sponsors' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'operations', label: 'Operations' }
] as const;

interface Props {
  employee: Employee;
  onClose: () => void;
}

const EmployeeEditModal = ({ employee, onClose }: Props) => {
  const dispatch = useAppDispatch();

  const [erole, setErole] = useState<string>(employee.erole || '');
  const [team, setTeam] = useState<string>(employee.team || '');
  const [office, setOffice] = useState<string>(employee.office || '');
  const [qualifications, setQualifications] = useState<string>(employee.qualifications || '');
  const [mobileNumber, setMobileNumber] = useState<string>(employee.mobile_number || '');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(
      EmployeeActions.updateEmployee(
        employee.id as number | string,
        {
          // Echo the identity fields so backend's `Employee.update` doesn't trip
          // on a presence validation when omitted.
          first_name: employee.first_name,
          last_name: employee.last_name,
          email: employee.email,
          // v3 fields
          erole,
          team: team === '' ? undefined : (team as Employee['team']),
          office,
          qualifications,
          mobile_number: mobileNumber
        } as Employee,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();

  return (
    <Modal title={`Edit ${fullName || 'employee'}`} onClose={onClose} footer={footer}>
      <div className="field">
        <span className="label">Job title (erole)</span>
        <input
          type="text"
          value={erole}
          onChange={(e) => setErole(e.target.value)}
          placeholder="e.g. Associate Director"
        />
      </div>

      <div className="field">
        <span className="label">Team</span>
        <select value={team} onChange={(e) => setTeam(e.target.value)}>
          {TEAM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Office</span>
        <input
          type="text"
          value={office}
          onChange={(e) => setOffice(e.target.value)}
          placeholder="e.g. Cape Town"
        />
      </div>

      <div className="field">
        <span className="label">Qualifications</span>
        <input
          type="text"
          value={qualifications}
          onChange={(e) => setQualifications(e.target.value)}
          placeholder="e.g. BCom (Hons), CA(SA), CFA"
        />
      </div>

      <div className="field">
        <span className="label">Phone</span>
        <input
          type="text"
          value={mobileNumber}
          onChange={(e) => setMobileNumber(e.target.value)}
          placeholder="+27 …"
        />
      </div>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Personal details (ID number, address, leave balance, employment documents) still live in the
        legacy form at <code>/employees/{employee.id}</code>.
      </p>
    </Modal>
  );
};

export default EmployeeEditModal;
